import { Link, useNavigate } from 'react-router-dom';
import { videos, categoryDisplayNames } from '@/mocks/home';

interface HotVideosProps {
  categorySlug?: string;
  limit?: number;
}

export default function HotVideos({ categorySlug, limit = 8 }: HotVideosProps) {
  const navigate = useNavigate();

  const filteredVideos = categorySlug
    ? videos.filter((v) => v.category === categorySlug)
    : videos;

  const displayVideos = filteredVideos.slice(0, limit);

  const handleVideoClick = (videoId: number) => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    navigate(`/category/${categorySlug || 'hot'}`);
  };

  return (
    <section className="w-full bg-background-50 py-10 md:py-14">
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between mb-6 md:mb-8">
          <div>
            <h4 className="text-sm font-semibold text-accent-500 uppercase tracking-wide">
              <a href="#hot-videos">热门推荐</a>
            </h4>
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mt-1">
              {categorySlug ? categoryDisplayNames[categorySlug] || '精选内容' : '大家都在看'}
            </h2>
          </div>
          {!categorySlug && (
            <Link
              to="/category/hot"
              className="flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap transition-colors"
            >
              查看全部 <i className="ri-arrow-right-line"></i>
            </Link>
          )}
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5" data-product-shop>
          {displayVideos.map((video) => (
            <button
              key={video.id}
              onClick={() => handleVideoClick(video.id)}
              className="group text-left cursor-pointer"
            >
              <div className="relative aspect-video rounded-lg overflow-hidden bg-background-200">
                <img
                  src={video.thumbnail}
                  alt={video.title}
                  title={`${video.title} - 白云视频高清在线播放`}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  loading="lazy"
                />
                <div className="absolute top-2 right-2 bg-black/70 text-white text-xs px-2 py-0.5 rounded">
                  {video.duration}
                </div>
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3">
                  <div className="flex items-center gap-2">
                    <span className="text-accent-400 text-xs font-semibold">
                      <i className="ri-star-fill text-[10px]"></i> {video.rating}
                    </span>
                    <span className="text-white/70 text-xs">{video.views}次播放</span>
                  </div>
                </div>
              </div>
              <h3 className="mt-2.5 text-sm font-medium text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-1">
                {video.title}
              </h3>
              <p className="mt-1 text-xs text-foreground-500 line-clamp-1">{video.description}</p>
              <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
                {video.tags.map((tag) => (
                  <span key={tag} className="text-[10px] px-1.5 py-0.5 rounded bg-secondary-100 text-secondary-700 whitespace-nowrap">
                    {tag}
                  </span>
                ))}
              </div>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}